import React from 'react';
import { Skeleton, SkeletonChart } from '../shared/Skeleton';

/**
 * Page-level loading shell, shaped like the page that is coming (spec v2 §11).
 *
 * The house pattern for an analytics dashboard is `SkeletonDashboard` — a KPI row, a
 * strip and chart blocks — because it mirrors the real layout so a load reads as
 * "already here" rather than as a spinner. `SkeletonDashboard` itself does not fit here:
 * it draws a tab strip, and §3 removed the tabs. So this is the same idea rebuilt to
 * this page's own anatomy, out of the same shimmer atoms.
 *
 * What it mirrors, top to bottom:
 *  - the verdict card, whose one long sentence is the largest text on screen
 *  - the "worth knowing" strip
 *  - the detail range control
 *  - the pinned headline card: four metric tiles over a 90-day trend chart
 *  - two ordinary panel cards: title, sentence, one visual
 *
 * Three cards rather than seventeen. A full-length skeleton would be a page of grey
 * taller than the viewport, which reads as broken rather than as loading; the shell only
 * has to hold the shape of the first screen.
 */
const PanelCardSkeleton = ({ chartHeight = 140 }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
    {/* Header strip — title on the left, chevron on the right. */}
    <div className="px-5 py-3 flex items-center justify-between gap-3">
      <Skeleton className="h-3 w-40" />
      <Skeleton className="h-5 w-5 rounded" />
    </div>
    {/* The sentence: two lines, the second short, like real prose wraps. */}
    <div className="px-5 pb-4 space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/5" />
    </div>
    <div className="px-5 pb-5">
      <SkeletonChart height={chartHeight} />
    </div>
  </div>
);

const SearchConsoleSkeleton = () => (
  <div className="space-y-6" role="status" aria-label="Loading" aria-live="polite">
    {/* Verdict card. */}
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-6 space-y-3">
      <Skeleton className="h-5 w-11/12" />
      <Skeleton className="h-5 w-2/3" />
      <div className="flex flex-wrap gap-3 pt-1">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-32" />
      </div>
    </div>

    {/* "Worth knowing" strip. */}
    <div className="space-y-2">
      <Skeleton className="h-3 w-28" />
      <div className="flex flex-wrap gap-4">
        <Skeleton className="h-4 w-44" />
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-4 w-40" />
      </div>
    </div>

    {/* Detail range control. */}
    <div className="flex flex-wrap items-center gap-3">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-3 w-52" />
    </div>

    {/* The pinned headline card: four tiles over its trend. */}
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="px-5 py-3 flex items-center justify-between gap-3">
        <Skeleton className="h-3 w-48" />
        <Skeleton className="h-5 w-5 rounded" />
      </div>
      <div className="px-5 pb-4 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <div className="px-5 pb-5 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            // Mirrors the real tile: top accent rule, label, big number.
            <div
              key={i}
              className="rounded-xl border border-gray-200 dark:border-gray-800 border-t-[3px] border-t-gray-200 dark:border-t-gray-700 bg-white dark:bg-gray-900 p-4 min-h-[112px] space-y-2"
            >
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-7 w-20" />
            </div>
          ))}
        </div>
        <SkeletonChart height={240} />
      </div>
    </div>

    <PanelCardSkeleton />
    <PanelCardSkeleton />

    <span className="sr-only">Loading…</span>
  </div>
);

export default SearchConsoleSkeleton;
